import {
  Box, Typography, TextField, InputAdornment, Card, CardContent, Grid, useMediaQuery, Button, Dialog, IconButton as MuiIconButton, Chip, Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Search as SearchIcon,
  AssignmentLate as AssignmentLateIcon,
  Autorenew as AutorenewIcon,
  DoneAll as DoneAllIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  AccessTime as AccessTimeIcon,
  AlarmOn as AlarmOnIcon,
  AlarmOff as AlarmOffIcon,
  PriorityHigh as PriorityHighIcon,
  WarningAmber as WarningIcon,
  LowPriority as LowPriorityIcon,
  Pause as PauseIcon
} from "@mui/icons-material";
import { useTheme, styled } from "@mui/material/styles";
import { toast } from "react-toastify";
import ToolTipTktCont from "../../../../GlobalFunction/ToolTipTktCont";
import Tooltip from "@mui/material/Tooltip";
import { useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import TicketDetailsPopup from "../RaiseServTkt/TktDetailsPopup";

const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case "high": return "#FF4D4F";
    case "medium": return "#FA8C16";
    case "low": return "#52C41A";
    default: return "#8C8C8C";
  }
};

const getDelaySeverityColor = (delay) => {
  if (!delay || delay === "On Time" || delay.trim() === "") return "#52C41A";
  const d = delay.trim().toLowerCase();
  const dayMatch = d.match(/(\d+)\s*(day|days)/);
  const hrMatch = d.match(/(\d+)\s*(hr|hour|hours)/);
  const minMatch = d.match(/(\d+)\s*(min|minute|minutes)/);
  const days = dayMatch ? parseInt(dayMatch[1]) : 0;
  const hours = hrMatch ? parseInt(hrMatch[1]) : 0;
  const minutes = minMatch ? parseInt(minMatch[1]) : 0;
  if (days === 0 && hours === 0 && minutes > 0) return "#52C41A";
  if (days > 0) return "#FF4D4F";
  if (hours > 3) return "#FF4D4F";
  if (hours > 0) return "#FFB302";
  return "#52C41A";
};

const StyledCard = styled(Card)(({ theme }) => ({
  flexGrow: 1,
  cursor: "pointer",
  borderRadius: "16px",
  background: "#fff",
  border: `2px solid ${theme.palette.grey[300]}`,
  transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
  "&:hover": {
    transform: "translateY(1px)",
    boxShadow: theme.shadows[2],
    borderColor: theme.palette.primary.light,
  },
  "&:active": {
    transform: "scale(0.98)",
  },
  position: "relative",
  overflow: "hidden",
  "&:before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "radial-gradient(circle, rgba(0,0,0,0.1) 0%, transparent 70%)",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:active:before": {
    opacity: 1,
  },
  "&.selected": {
    boxShadow: theme.shadows[8],
  },
}));

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getStatusIcon = (status, theme) => {
  switch (status) {
    case "O":
      return <AssignmentLateIcon sx={{ color: theme.palette.warning.dark, fontSize: "1.2rem" }} />;
    case "I":
      return <AutorenewIcon sx={{ color: theme.palette.warning.dark, fontSize: "1.2rem" }} />;
    case "H":
      return <PauseIcon sx={{ color: theme.palette.warning.dark, fontSize: "1.2rem" }} />;
    case "R":
      return <DoneAllIcon sx={{ color: theme.palette.warning.dark, fontSize: "1.2rem" }} />;
    case "C":
      return <CancelIcon sx={{ color: theme.palette.warning.dark, fontSize: "1.2rem" }} />;
    default:
      return null;
  }
};

const fixImageUrl = (url) => {
  if (!url) return null;
  return url.replace(/^http:\/([^/])/, "http://$1");
};

const FilterChip = styled(Chip)(({ theme, filtertype, selected }) => {
  const colors = {
    high: { main: "#ff7961", light: "#ff9a7a", dark: "#f4511e" },
    medium: { main: "#ffb74d", light: "#ffcc80", dark: "#f57c00" },
    low: { main: "#64b5f6", light: "#90caf9", dark: "#1e88e5" },
    ontime: { main: "#81c784", light: "#a5d6a7", dark: "#43a047" },
    delayed: { main: "#e57373", light: "#ffcdd2", dark: "#e53935" },
  };
  const color = colors[filtertype] || colors.low;
  return {
    height: 36,
    fontWeight: 700,
    fontSize: "0.92rem",
    borderRadius: "24px",
    minWidth: 80,
    padding: "0 4px",
    cursor: "pointer",
    transition: "all 0.25s ease",
    border: "none",
    backgroundColor: selected ? color.dark : color.light,
    color: selected ? "white" : color.dark,
    border: selected ? `2px solid ${theme.palette.primary.main}` : "none",
    boxShadow: selected
      ? `0 8px 25px ${color.dark}60`
      : "0 3px 10px rgba(0,0,0,0.08)",
    "& .MuiChip-icon": {
      color: selected ? "white" : color.dark,
    },
    "&:hover": {
      backgroundColor: `${color.dark} !important`,
      color: "white",
      transform: "translateY(-2px)",
      boxShadow: `0 10px 20px ${color.dark}50 !important`,
      "& .MuiChip-icon": {
        color: "white !important",
      },
    },
    "&:active": {
      transform: "scale(0.98)",
    },
  };
});

const MultipleCards = ({
  rows,
  searchTerm,
  setSearchTerm,
  selectedTicket,
  handleRowClick,
  getStatusChip,
  selectedRows,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [zoomedTicketId, setZoomedTicketId] = useState(null);
  const [zoomedImageUrl, setZoomedImageUrl] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState("");
  const [slaFilter, setSlaFilter] = useState("");
  const [detailPopupOpen, setDetailPopupOpen] = useState(false);
  const [selectedTicketDetail, setSelectedTicketDetail] = useState(null);

  const handleCardClick = (row) => {
    setSelectedTicketDetail(row);
    setDetailPopupOpen(true);
  };

  const handleImageClick = (e, ticketId, imageUrl) => {
    e.stopPropagation();
    if (imageUrl) {
      setZoomedTicketId((prev) => (prev === ticketId ? null : ticketId));
      setZoomedImageUrl((prev) => (prev === imageUrl ? null : imageUrl));
    }
  };

  const filteredRows = Array.isArray(rows)
    ? rows.filter((row) => {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        formatDate(row.TktDate).toLowerCase().includes(search) ||
        formatTime(row.TktDate).toLowerCase().includes(search) ||
        (row.TicketNo?.toLowerCase().includes(search)) ||
        (row.ServiceName?.toLowerCase().includes(search)) ||
        (row.CategoryName?.toLowerCase().includes(search)) ||
        (row.PropName?.toLowerCase().includes(search)) ||
        (row.ServiceProvider?.toLowerCase().includes(search)) ||
        getStatusChip(row.TicketSt).props.label.toLowerCase().includes(search);
      const matchesPriority = !priorityFilter ||
        (priorityFilter === "High" && row.TicketServName?.toLowerCase().includes("high")) ||
        (priorityFilter === "Medium" && row.TicketServName?.toLowerCase().includes("medium")) ||
        (priorityFilter === "Low" && row.TicketServName?.toLowerCase().includes("low"));
      const isOnTime = () => {
        if (!row.DelayTime || row.DelayTime === "On Time" || row.DelayTime.trim() === "") return true;
        const d = row.DelayTime.trim();
        if (/(day|days|hr|hour)/i.test(d)) return false;
        if (/(min|minute)/i.test(d)) return true;
        if (d.includes("0") && /(min|day)/i.test(d)) return true;
        return true;
      };
      const matchesSLA = !slaFilter ||
        (slaFilter === "OnTime" && isOnTime()) ||
        (slaFilter === "Delayed" && !isOnTime());
      return matchesSearch && matchesPriority && matchesSLA;
    })
    : [];

  const RowItem = ({ label, value, useTooltip = true }) => (
    <Box sx={{ display: "flex", alignItems: "baseline", mb: 0.5 }}>
      <Typography
        component="span"
        variant="body2"
        sx={{
          fontWeight: 700,
          whiteSpace: "nowrap",
          mr: 0.5,
          fontSize: { xs: "0.99rem", sm: "0.99rem", md: "0.85rem" },
        }}
      >
        {label}:
      </Typography>
      {useTooltip ? (
        <ToolTipTktCont title={value}>{value || "N/A"}</ToolTipTktCont>
      ) : (
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontSize: { xs: "0.99rem", sm: "0.99rem", md: "0.85rem" },
            wordBreak: "break-word",
          }}
        >
          {value || "N/A"}
        </Typography>
      )}
    </Box>
  );

  const hasActiveFilter = priorityFilter || slaFilter;
  return (
    <Box
      sx={{
        paddingBlock: 0,
        paddingBottom: 2,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: { xs: "stretch", md: "center" },
          mb: 1.5,
        }}
      >
        <Box sx={{
          flex: { xs: "1 1 100%", md: "1" },
          maxWidth: { xs: "100%", md: "50%" },
        }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search Tickets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: 40,
                borderRadius: "16px",
                fontSize: "0.95rem",
                backgroundColor: "#f8fafc",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              },
            }}
          />
        </Box>
        <Stack
          direction={{ xs: "row", md: "row" }}
          spacing={0.5}
          flexWrap="wrap"
          justifyContent={{ xs: "center", md: "flex-end" }}
          sx={{
            gap: 0.2,
            width: { xs: "100%", md: "auto" },
            "& > *": {
              flex: { xs: "1 1 45%", md: "0 0 auto" },
              minWidth: { xs: "120px", md: "auto" },
            },
          }}
        >
          <FilterChip
            icon={<PriorityHighIcon />}
            label="High Priority"
            filtertype="high"
            selected={priorityFilter === "High"}
            onClick={() => {
              setPriorityFilter("High");
              setSlaFilter("");
            }}
            sx={{
              "&&": {
                mb: { xs: 0.3, md: 0 },
              },
            }}
          />
          <FilterChip
            icon={<WarningIcon />}
            label="Medium Priority"
            filtertype="medium"
            selected={priorityFilter === "Medium"}
            onClick={() => {
              setPriorityFilter("Medium");
              setSlaFilter("");
            }}
            sx={{
              "&&": {
                mb: { xs: 0.3, md: 0 },
              },
            }}
          />
          <FilterChip
            icon={<LowPriorityIcon />}
            label="Low Priority"
            filtertype="low"
            selected={priorityFilter === "Low"}
            onClick={() => {
              setPriorityFilter("Low");
              setSlaFilter("");
            }}
            sx={{
              "&&": {
                mb: { xs: 0.3, md: 0 },
              },
            }}
          />
          {/* SLA Filters */}
          <FilterChip
            icon={<AlarmOnIcon />}
            label="On-Time"
            filtertype="ontime"
            selected={slaFilter === "OnTime"}
            onClick={() => {
              setSlaFilter("OnTime");
              setPriorityFilter("");
            }}
            sx={{
              "&&": {
                mb: { xs: 0.3, md: 0 },
              },
            }}
            clickable
            variant={slaFilter === "OnTime" ? "filled" : "outlined"}
          />
          <FilterChip
            icon={<AlarmOffIcon />}
            label="Delayed"
            filtertype="delayed"
            selected={slaFilter === "Delayed"}
            onClick={() => setSlaFilter(slaFilter === "Delayed" ? "" : "Delayed")}
            clickable
            variant={slaFilter === "Delayed" ? "filled" : "outlined"}
          />
          {/* Clear All */}
          {(priorityFilter || slaFilter) && (
            <FilterChip
              label="Clear Filters"
              onClick={() => {
                setPriorityFilter("");
                setSlaFilter("");
              }}
              sx={{
                backgroundColor: "#ffebee",
                color: "#c62828",
                border: "2px solid #ffcdd2",
                fontWeight: 700,
                "&:hover": { backgroundColor: "#ffcdd2", color: "#b71c1c" },
              }}
            />
          )}
        </Stack>
      </Box>
      {filteredRows.length > 0 ? (
        <Grid container spacing={2} sx={{ minHeight: "320px" }}>
          {filteredRows.map((row) => {
            const imageUrl = fixImageUrl(row.ImgName);
            const hasImage = !!imageUrl;
            const isZoomed = zoomedTicketId === row.TicketId;

            return (
              <Grid item xs={12} sm={6} md={3} key={row.TicketId} sx={{ display: "flex" }}>
                <StyledCard
                  className={row.TicketId === selectedTicket?.TicketId ? "selected" : ""}
                  onClick={() => handleCardClick(row)}
                >
                  <CardContent sx={{ padding: "16px !important" }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      mb={1}
                      gap={0}
                      flexWrap="nowrap"
                      sx={{ overflow: "hidden", whiteSpace: "nowrap", marginX: { xs: -0.5, sm: 0 } }}
                    >
                      <Box display="flex" alignItems="center" gap={0.3}>
                        {getStatusIcon(row.TicketSt, theme)}
                        <Typography
                          variant="subtitle1"
                          color="text.primary"
                          fontWeight="bold"
                          sx={{ fontSize: { xs: "1rem", sm: "1rem", md: "0.99rem" } }}
                        >
                          Tkt No. {row.TicketNo}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: "1rem", sm: "0.85rem" },
                            overflow: "visible",
                            whiteSpace: "normal",
                          }}
                        >
                          {getStatusChip(row.TicketSt)}
                        </Typography>
                        {hasImage && (
                          <PhotoCameraIcon
                            onClick={(e) => handleImageClick(e, row.TicketId, imageUrl)}
                            sx={{
                              fontSize: { xs: "1.5rem", sm: "1.8rem" },
                              color: theme.palette.info.main,
                              cursor: "pointer",
                              ml: 0.5,
                              transition: "transform 0.3s ease-in-out, color 0.3s ease-in-out",
                              transform: isZoomed ? "scale(1)" : "scale(1)",
                              "&:hover": {
                                transform: "scale(1.1)",
                                color: theme.palette.info.dark,
                              },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    <RowItem label="RaisedBy" value={row.Name} />
                    <RowItem label="Date" value={`${formatDate(row.TktDate)} | ${formatTime(row.TktDate)}`} useTooltip={false} />
                    <RowItem label="Complain" value={row.ServiceName} />
                    <RowItem label="Property" value={row.PropName} />
                    <RowItem label="Category" value={row.CategoryName} />
                    <RowItem label="TktFor" value={`${row.TktFor || "N/A"} | ${row.BedNo || "N/A"}`} />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0.5, flexWrap: "wrap" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                          Priority:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "bold",
                            color: getPriorityColor(row.TicketServName),
                            fontSize: "0.9rem",
                            textShadow: "0 0 8px rgba(0,0,0,0.2)",
                          }}
                        >
                          {row.TicketServName || "N/A"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                          Lead Time:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: row.LeadTime === "0.00 Min" ? "#52C41A" : "#ff1818",
                            fontSize: "0.85rem",
                          }}
                        >
                          {row.LeadTime ? `${parseFloat(row.LeadTime).toFixed(2)} Hr` : "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0.5, flexWrap: "wrap" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                          Start:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.9rem",
                            textShadow: "0 0 8px rgba(0,0,0,0.2)",
                          }}
                        >
                          {row.TktDate ? format(new Date(row.TktDate), 'dd-MM-yyyy HH:mm') : "N/A"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                          End Days:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.85rem",
                          }}
                        >
                          {row.EndDays || "0.00"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0.5, flexWrap: "wrap" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                          Tag:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "#722ed1" }}>
                          {row.TicketTagName || "N/A"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                          Delay:
                        </Typography>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.2,
                            borderRadius: "12px",
                            backgroundColor: getDelaySeverityColor(row.DelayTime),
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "0.8rem",
                            minWidth: "60px",
                            textAlign: "center",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                          }}
                        >
                          {row.DelayTime || "On Time"}
                        </Box>
                      </Box>
                    </Box>
                    <RowItem label="Provider" value={row.ServiceProvider} />
                  </CardContent>
                </StyledCard>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "300px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            border: "1px dashed #ccc",
            borderRadius: "12px",
            mt: 0,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No Records Found
          </Typography>
        </Box>
      )}
      {zoomedTicketId && zoomedImageUrl && (
        <Dialog
          open={true}
          onClose={() => {
            setZoomedTicketId(null);
            setZoomedImageUrl(null);
          }}
          maxWidth="md"
          fullWidth
          BackdropProps={{
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.95)' },
          }}
          PaperProps={{
            sx: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              overflow: 'hidden',
              m: 2,
            },
          }}
        >
          <MuiIconButton
            onClick={() => {
              setZoomedTicketId(null);
              setZoomedImageUrl(null);
            }}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              color: 'black',
              '&:hover': { bgcolor: 'white' },
              zIndex: 10,
              boxShadow: 3,
            }}
          >
            <CloseIcon />
          </MuiIconButton>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '90vh',
              cursor: 'zoom-out',
            }}
            onClick={() => {
              setZoomedTicketId(null);
              setZoomedImageUrl(null);
            }}
          >
            <Box
              component="img"
              src={zoomedImageUrl}
              alt="Zoomed Ticket"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
              sx={{
                maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', borderRadius: 2, boxShadow: 10, userSelect: 'none',
              }}
            />
          </Box>
        </Dialog>
      )}
      <TicketDetailsPopup
        open={detailPopupOpen}
        onClose={() => setDetailPopupOpen(false)}
        ticket={selectedTicketDetail}
      />
    </Box>
  );
};
export default MultipleCards;